import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import * as Sentry from '@sentry/react'
import Btn from 'components/buttons/Btn'
import ConfirmModal from 'components/ConfirmModal'
import { FormTip } from 'components/form'
import { Alert, Badge, Card, Col, Form, ListGroup, Row } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { toggleShipday, useAccount } from 'state/Account'
import { useServerConstants } from 'state/Constants'
import { getIsSingleEnabledRestaurant, toggleAutoDispatch, useActiveRestaurant } from 'state/Restaurants'
import { Alerts, useModalState } from 'utils'

export default function DeliverySettings() {
	const { t }    = useTranslation()
	const dispatch = useDispatch()

	const account      = useAccount()
	const activeRest   = useActiveRestaurant()
	const isSingleRest = useSelector(getIsSingleEnabledRestaurant)
	const CONSTANTS    = useServerConstants().VALUES

	const requestModal      = useModalState()
	const cancelModal       = useModalState()
	const autoDispatchModal = useModalState()

	if (isSingleRest && !activeRest) {
		//FIXME [sc-53194] there's an edge case where multi-loc accounts with only one unlocked restaurant will end up with "all locations" selected (hence, no active restaurant). Hopefully, this will be fixed by the changes from this commit... but may never affect broken accounts if there's no restaurant changes fr; thus, after this version is in widespread use, it would be safe to remove this check and the alert from doToggleAutoDispatch. Nonetheless, also remember to check if there's no more Sentry reports after a while (if there was every any).
		Sentry.captureMessage('Multi-loc account with locked restaurants and no active one!', 'error')
	}

	function openModal() {
		if (account.isShipdayRequestPending(CONSTANTS)) {
			cancelModal.doOpen()
		} else {
			requestModal.doOpen()
		}
	}

	function doCheckAutoDispatch() {
		//no need for event.preventDefault() because [1] it's not preventable and [2] it's a controlled input
		autoDispatchModal.doOpen()
	}

	async function doToggle() {
		await dispatch(toggleShipday) //TODO error handling?
	}

	function doToggleAutoDispatch() {
		//FIXME this is NOT meant to work for multi-restaurant accounts!!! If auto-dispatch ends up indeed as a restaurant-based setting, this should show a list of locations and their auto-dispatch statuses. The usual location selector doesn't seem like a good UI, since it might get hidden in the side menu and the user won't make the connection here, since everything else in this screen is tied to the device or the AccRep
		if (!activeRest) {
			Sentry.captureMessage('Multi-loc account with locked restaurants and no active one tried to enable auto-dispatch?!', 'error')
			Alerts.danger('Sorry, there is something wrong with the restaurant details inside the app... Could you log out, log in, and try this again?')
			return false
		}
		return dispatch(toggleAutoDispatch(activeRest.id, !activeRest.shipday_auto_dispatch_enabled))
	}

	let badgeColor, badgeText, description, actionText
	switch (account.shipday_enabled_status) {
		default:
		case CONSTANTS.ACCOUNT.SHIPDAY_STATUS.DISABLED:
			badgeText   = t('Inactive')
			badgeColor  = 'secondary'
			description = t('To activate Third-Party Delivery Services, send us a request with the button below. Our Support Team will contact you for further assistance.')
			actionText  = t('Request services')
			break

		case CONSTANTS.ACCOUNT.SHIPDAY_STATUS.ENABLED:
			badgeText   = t('Active')
			badgeColor  = 'success'
			description = t('Third-Party Delivery Services are enabled. You can request a delivery driver on the order\'s page.')
			actionText  = t('Request deactivation')
			break

		case CONSTANTS.ACCOUNT.SHIPDAY_STATUS.REQUESTED:
			badgeText   = t('Requested')
			badgeColor  = 'warning'
			description = t('Your activation request has been submitted. Upon confirmation with the Support Team, the feature will be enabled.')
			actionText  = t('Cancel activation')
			break

		case CONSTANTS.ACCOUNT.SHIPDAY_STATUS.DISABLE_REQUESTED:
			badgeText   = t('Active')
			badgeColor  = 'success'
			description = t('Your deactivation request has been submitted. Upon confirmation with the Support Team, the feature will be disabled.')
			actionText  = t('Cancel deactivation')
			break
	}

	return <>
		<Card id="card-delivery">
			<Card.Header>
				<Row>
					<Col className="d-flex align-items-center">
						<Icon icon="car-side"/> {t('Delivery services')}
					</Col>
					<Col xs="auto" className="embedded-btn">
						<Badge className="mr-3 px-2" variant={badgeColor}>{badgeText}</Badge>
					</Col>
				</Row>
			</Card.Header>
			<ListGroup variant="flush">
				<ListGroup.Item>
					<FormTip>{description}</FormTip>
					<Btn onClick={openModal} swap variant="outline-dark" className="mt-2">{actionText}</Btn>
				</ListGroup.Item>
				<ListGroup.Item>
					<Form.Group>
						<Form.Switch label={t('Auto-dispatch orders')} id="enableAutoDispatching" onChange={doCheckAutoDispatch}
						             checked={activeRest?.shipday_auto_dispatch_enabled} disabled={!isSingleRest}/>
						<FormTip>
							{isSingleRest?
								t('Drivers will be automatically assigned as soon as orders are placed by customers.')
								:
								<>
									<Icon icon="exclamation-triangle"/>{' '}
									{t('This feature is not yet available for accounts with multiple locations.')}{' '}
									{t('CONTACT_INFO')}
								</>
							}
						</FormTip>
					</Form.Group>
				</ListGroup.Item>
			</ListGroup>
		</Card>

		<ConfirmModal {...requestModal} doSubmit={doToggle} size="lg"
		              confirmTexts={[t('Confirm'), t('Confirming')]}
		              title={t('Request services')}>
			<Trans>
				Please, confirm your request for third-party delivery services.<br/>
				Our Support Team will contact you soon for further assistance.
			</Trans>
		</ConfirmModal>

		<ConfirmModal {...cancelModal} doSubmit={doToggle} size="lg" dangerous
		              cancelText={t('No, keep request')}
		              confirmTexts={[t('Yes, cancel'), t('Canceling')]}
		              title={t('Cancel request')}>
			{t('Are you sure you want to cancel the request for third-party delivery services?')}
		</ConfirmModal>

		<ConfirmModal {...autoDispatchModal} doSubmit={doToggleAutoDispatch} size="lg"
		              dangerous={!!activeRest?.shipday_auto_dispatch_enabled}
		              confirmTexts={activeRest?.shipday_auto_dispatch_enabled?
			              [t('Disable it'), t('Disabling', { context: '...a feature' })] :
			              [t('Yes, activate it'), t('Activating', { context: '...a feature' })]
		              }
		              title={t('Auto-dispatching')}>
			<p>
				{activeRest?.shipday_auto_dispatch_enabled?
					t('When you disable auto-dispatch, you have the freedom to decide how to handle your delivery orders, whether through your own drivers or a third-party service.')
					:
					t('Enabling auto-dispatch means drivers will be automatically assigned as soon as orders are placed by customers, incurring third-party delivery charges for your restaurant instantly.')}
			</p>

			<Alert variant="info">
				<Alert.Heading as="h5">{t('Please remember:')}</Alert.Heading>
				<ol style={{ listStyleType: 'lower-alpha' }}>
					{activeRest?.shipday_auto_dispatch_enabled?
						<Trans>
							<li>You'll need to <strong>manually confirm</strong> your choice for each order;</li>
							<li>Make sure to request a third-party driver in time to avoid delays in order fulfillment.</li>
						</Trans>
						:
						<Trans>
							<li>Disable online ordering if necessary;</li>
							<li>Reach out to us to halt delivery orders when unable to fulfill them;</li>
							<li>Or simply deactivate auto-dispatching and switch to manual mode as necessary.</li>
						</Trans>
					}
				</ol>
			</Alert>

			<p><b>
				{activeRest?.shipday_auto_dispatch_enabled?
					t('Are you sure you wish to disable auto-dispatching?')
					:
					t('Are you sure you wish to activate auto-dispatching?')}
			</b></p>
		</ConfirmModal>

	</>
}
